import React, { useState, useCallback, useMemo } from "react";
import {
  EditorState,
  ContentState,
  Modifier,
  convertToRaw,
  convertFromHTML,
} from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Chip,
  Menu,
  MenuItem,
} from "@mui/material";
import { DataObjectOutlined, KeyboardArrowDown } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { RootState } from "../../store/Index";
import draftToHtml from "draftjs-to-html";

interface RichTextEditorProps {
  initialContent: string;
  onSave: (htmlContent: string) => Promise<void> | void;
  title: string;
  placeholder?: string;
  saveButtonText?: string;
  onChange?: (htmlContent: string) => void;
  placeholders?: string[];
  showSaveButton?: boolean;
  /** How placeholder tokens are offered for insertion. Defaults to the existing chip row. */
  placeholderDisplay?: "chips" | "dropdown";
  /** Toolbar option set. "full" is the existing 8-group toolbar. "compact" trims to the essentials
   *  and visually separates them into labeled groups instead of one flat row of boxed buttons. */
  toolbarPreset?: "full" | "compact";
  /** Content area sizing. "fixed" is the existing 520px + internal scroll behavior.
   *  "auto" removes the fixed height/internal scroll so the editor grows with its content —
   *  use when the parent page itself provides the only scroll container. */
  contentHeight?: "fixed" | "auto" | "fill";
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  initialContent,
  onSave,
  title,
  placeholder = "Start typing here...",
  saveButtonText = "Save",
  onChange,
  placeholders = [],
  showSaveButton = true,
  placeholderDisplay = "chips",
  toolbarPreset = "full",
  contentHeight = "fixed",
}) => {
  const theme = useSelector((state: RootState) => state.theme);

  const [editorState, setEditorState] = useState(() => {
    try {
      if (initialContent) {
        const blocksFromHTML = convertFromHTML(initialContent);
        const contentState = ContentState.createFromBlockArray(
          blocksFromHTML.contentBlocks,
          blocksFromHTML.entityMap,
        );
        return EditorState.createWithContent(contentState);
      }
      return EditorState.createEmpty();
    } catch (error) {
      console.error("Error initializing editor state:", error);
      return EditorState.createEmpty();
    }
  });

  const [saving, setSaving] = useState(false);
  const [placeholderMenuAnchor, setPlaceholderMenuAnchor] =
    useState<HTMLElement | null>(null);

  const toolbarOptions = useMemo(() => {
    if (toolbarPreset === "compact") {
      return {
        options: ["blockType", "inline", "list", "link"],
        inline: { options: ["bold", "italic", "underline"] },
        blockType: {
          options: ["Normal", "H1", "H2", "H3", "Blockquote"],
        },
        list: { options: ["unordered", "ordered"] },
      };
    }
    return {
      options: [
        "inline",
        "blockType",
        "list",
        "textAlign",
        "link",
        "image",
        "remove",
        "history",
      ],
      inline: { options: ["bold", "italic", "underline", "strikethrough"] },
      blockType: {
        options: ["Normal", "H1", "H2", "H3", "H4", "H5", "H6", "Blockquote"],
      },
      list: { options: ["unordered", "ordered"] },
      textAlign: { options: ["left", "center", "right", "justify"] },
    };
  }, [toolbarPreset]);

  const handleEditorChange = useCallback(
    (newEditorState: EditorState) => {
      setEditorState(newEditorState);
      if (onChange) {
        onChange(draftToHtml(convertToRaw(newEditorState.getCurrentContent())));
      }
    },
    [onChange],
  );

  const handleSaveClick = useCallback(async () => {
    try {
      setSaving(true);
      const rawContent = convertToRaw(editorState.getCurrentContent());
      const htmlContent = draftToHtml(rawContent);
      await onSave(htmlContent);
    } catch (err) {
      console.error("Failed to save:", err);
    } finally {
      setSaving(false);
    }
  }, [editorState, onSave]);

  const insertPlaceholder = useCallback(
    (placeholder: string) => {
      const content = editorState.getCurrentContent();
      const selection = editorState.getSelection();
      const nextContent = Modifier.replaceText(
        content,
        selection,
        `{${placeholder}}`,
        editorState.getCurrentInlineStyle(),
      );
      const nextState = EditorState.push(
        editorState,
        nextContent,
        "insert-characters",
      );
      const focusedState = EditorState.forceSelection(
        nextState,
        nextContent.getSelectionAfter(),
      );
      setEditorState(focusedState);
      onChange?.(draftToHtml(convertToRaw(nextContent)));
    },
    [editorState, onChange],
  );

  const handlePlaceholderMenuSelect = useCallback(
    (placeholder: string) => {
      insertPlaceholder(placeholder);
      setPlaceholderMenuAnchor(null);
    },
    [insertPlaceholder],
  );

  const useFixedHeight = contentHeight === "fixed" && placeholders.length > 0;
  const fillAvailableHeight = contentHeight === "fill";

  return (
    <Paper
      sx={{
        p: 3,
        backgroundColor: theme.primary_colors[800],
        boxShadow: 0,
        ...(fillAvailableHeight && {
          height: "100%",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }),
      }}
    >
      {title && (
        <Typography
          variant="h5"
          sx={{ mb: 2, color: theme.primary_colors[700] }}
        >
          {title}
        </Typography>
      )}
      <Box
        sx={{
          position: "relative",
          ...(fillAvailableHeight && {
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            ".rdw-editor-wrapper": {
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            },
          }),
          ".rdw-editor-toolbar": {
            backgroundColor: theme.primary_colors[900],
            borderColor: theme.primary_colors[50],
            color: theme.primary_colors[700],
            ...(toolbarPreset === "compact" && {
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: "2px",
              minHeight: "42px",
              margin: 0,
              padding: "4px 154px 4px 5px",
            }),
          },
          ".rdw-option-wrapper, .rdw-dropdown-wrapper": {
            backgroundColor: theme.primary_colors[900],
            color: theme.primary_colors[700],
            borderColor: theme.primary_colors[50],
          },
          ".rdw-option-wrapper:hover, .rdw-option-active": {
            backgroundColor: theme.primary_colors[600],
          },
          // Compact preset: give each button-group its own light container instead of
          // every individual button carrying the same border — restores visual grouping.
          ...(toolbarPreset === "compact" && {
            ".rdw-option-wrapper": {
              minWidth: "28px",
              height: "28px",
              margin: "0 1px",
              padding: "3px",
            },
            ".rdw-dropdown-wrapper": {
              height: "30px",
              margin: "0 1px",
              fontSize: "0.78rem",
            },
            ".rdw-block-wrapper, .rdw-inline-wrapper, .rdw-list-wrapper, .rdw-link-wrapper":
              {
                display: "flex",
                alignItems: "center",
                gap: "1px",
                padding: "1px",
                borderRadius: "5px",
                border: `1px solid ${theme.primary_colors[50]}`,
                margin: 0,
                marginRight: "3px",
              },
          }),
          ".rdw-editor-main": {
            minHeight: "200px",
            ...(useFixedHeight && {
              height: "520px",
              maxHeight: "520px",
              overflowY: "auto",
            }),
            ...(fillAvailableHeight && {
              flex: 1,
              minHeight: 0,
              height: "auto",
              overflowY: "auto",
            }),
            backgroundColor: theme.primary_colors[900],
            color: theme.primary_colors[700],
            border: `1px solid ${theme.primary_colors[300]}`,
            padding: "8px",
          },
          ".DraftEditor-root": {
            color: theme.primary_colors[700],
            "& p, & h1, & h2, & h3, & h4, & h5, & h6": {
              color: theme.primary_colors[700],
            },
            "& a": { color: theme.primary_colors[10] },
            lineHeight: 1.7,
            fontSize: { xs: "0.875rem", md: "1rem" },
          },
        }}
      >
        {placeholders.length > 0 && placeholderDisplay === "chips" && (
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 5,
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 0.75,
              minHeight: "48px",
              p: 1,
              bgcolor: theme.primary_colors[900],
              border: `1px solid ${theme.primary_colors[300]}`,
            }}
          >
            <Typography variant="caption" sx={{ mr: 0.5, fontWeight: 600 }}>
              Insert stream value:
            </Typography>
            {placeholders.map((placeholder) => (
              <Chip
                key={placeholder}
                label={`{${placeholder}}`}
                size="small"
                variant="outlined"
                onMouseDown={(event) => {
                  event.preventDefault();
                  insertPlaceholder(placeholder);
                }}
              />
            ))}
          </Box>
        )}

        {placeholders.length > 0 && placeholderDisplay === "dropdown" && (
          <Box
            sx={{
              position: "absolute",
              top: 5,
              right: 8,
              zIndex: 6,
              display: "flex",
              justifyContent: "flex-end",
              p: 0,
              bgcolor: "transparent",
            }}
          >
            <Button
              size="small"
              variant="outlined"
              startIcon={<DataObjectOutlined fontSize="small" />}
              endIcon={<KeyboardArrowDown fontSize="small" />}
              onClick={(event) => setPlaceholderMenuAnchor(event.currentTarget)}
              sx={{
                minHeight: 30,
                px: 1.25,
                fontSize: "0.72rem",
                whiteSpace: "nowrap",
              }}
            >
              Insert field
            </Button>
            <Menu
              anchorEl={placeholderMenuAnchor}
              open={Boolean(placeholderMenuAnchor)}
              onClose={() => setPlaceholderMenuAnchor(null)}
            >
              {placeholders.map((placeholder) => (
                <MenuItem
                  key={placeholder}
                  onClick={() => handlePlaceholderMenuSelect(placeholder)}
                >
                  {`{${placeholder}}`}
                </MenuItem>
              ))}
            </Menu>
          </Box>
        )}

        <Editor
          editorState={editorState}
          onEditorStateChange={handleEditorChange}
          toolbar={toolbarOptions}
          placeholder={placeholder}
        />
      </Box>
      {showSaveButton && (
        <Button
          variant="contained"
          sx={{
            mt: 3,
            backgroundColor: theme.primary_colors[500],
            cursor: "pointer",
          }}
          onClick={handleSaveClick}
          disabled={saving}
          startIcon={
            saving ? <CircularProgress size={18} color="inherit" /> : null
          }
        >
          {saving ? "Saving..." : saveButtonText}
        </Button>
      )}
    </Paper>
  );
};

export default RichTextEditor;
